import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Group,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { trpcSlice } from "../../../trpcSlice";

interface IFormulaCounterMaterializationPreviewItem {
  issueObjectId: string;
  issueId: number;
  groupKey: string;
  groupValues: Record<string, unknown>;
  automaticCounterValue: number;
  newManualCounterValue: number;
  existingCounterValue: number | null;
}

interface IFormulaCounterMaterializationPlan {
  issueBoardId: string;
  formulaAttributeKey: string;
  dryRun: boolean;
  groupAttributeKeys: string[];
  totalIssues: number;
  totalGroups: number;
  issuesWithExistingCounter: number;
  issuesToSet: number;
  matchedCount: number;
  modifiedCount: number;
  preview: IFormulaCounterMaterializationPreviewItem[];
}

const appAdminFormulaCounterApi = trpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    materializeFormulaCounters: builder.mutation<
      IFormulaCounterMaterializationPlan,
      { issueBoardId: string; formulaAttributeKey: string; dryRun?: boolean }
    >({
      query: (body) => ({
        url: "/appadmin/issue-formula-counter-materialization/materialize",
        method: "POST",
        body: {
          issueBoardId: body.issueBoardId.trim(),
          formulaAttributeKey: body.formulaAttributeKey.trim(),
          dryRun: body.dryRun ?? true,
        },
      }),
    }),
  }),
});

const { useMaterializeFormulaCountersMutation } = appAdminFormulaCounterApi;

export const FormulaCounterMaterializationAdmin = () => {
  const [issueBoardId, setIssueBoardId] = useState("");
  const [formulaAttributeKey, setFormulaAttributeKey] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [materialize, { data, error, isLoading }] = useMaterializeFormulaCountersMutation();

  const runMaterialization = () => {
    materialize({
      issueBoardId,
      formulaAttributeKey,
      dryRun,
    });
  };

  const errorMessage =
    (error as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
    (error as { message?: string } | undefined)?.message;

  return (
    <Card withBorder shadow="sm" p="md">
      <Stack gap="sm">
        <Title order={3}>Materialiser løpnummer</Title>
        <Text size="sm" c="dimmed">Kopierer dagens automatiske formel-løpnummer til manuelle løpnummer.</Text>

        <Group align="flex-end">
          <TextInput
            label="Issueboard ID"
            value={issueBoardId}
            onChange={(event) => setIssueBoardId(event.currentTarget.value)}
            style={{ flex: "1 1 28rem" }}
          />
          <TextInput
            label="Formelkolonne"
            placeholder="F.eks. A"
            value={formulaAttributeKey}
            onChange={(event) => setFormulaAttributeKey(event.currentTarget.value.toUpperCase())}
            w="12rem"
          />
          <Checkbox
            label="Dry-run"
            checked={dryRun}
            onChange={(event) => setDryRun(event.currentTarget.checked)}
            pb={8}
          />
          <Button
            loading={isLoading}
            disabled={issueBoardId.trim().length === 0 || formulaAttributeKey.trim().length === 0}
            onClick={runMaterialization}
          >
            {dryRun ? "Kjør dry-run" : "Skriv manuelle løpnummer"}
          </Button>
        </Group>

        {errorMessage != null && (
          <Alert color="red" variant="light">
            {errorMessage}
          </Alert>
        )}

        {data != null && (
          <Stack gap="sm">
            <Group gap="xs">
              <Badge variant="light">Formel {data.formulaAttributeKey}</Badge>
              <Badge color={data.dryRun ? "blue" : "green"} variant="light">
                {data.dryRun ? "Dry-run" : "Skrevet"}
              </Badge>
              <Badge variant="light">{data.totalIssues} saker</Badge>
              <Badge variant="light">{data.totalGroups} grupper</Badge>
              <Badge variant="light">{data.issuesWithExistingCounter} har manuelt nummer</Badge>
              {!data.dryRun && <Badge variant="light">{data.matchedCount} matchet</Badge>}
              {!data.dryRun && <Badge variant="light">{data.modifiedCount} endret</Badge>}
            </Group>

            <Text size="sm">
              Gruppefelter:{" "}
              {data.groupAttributeKeys.length > 0 ? data.groupAttributeKeys.join(", ") : "Ingen, alle saker i én gruppe"}
            </Text>

            <ScrollArea h={360} type="auto">
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Sak-ID</Table.Th>
                    <Table.Th>Automatisk løpnummer</Table.Th>
                    <Table.Th>Nytt manuelt løpnummer</Table.Th>
                    <Table.Th>Eksisterende manuelt</Table.Th>
                    <Table.Th>Gruppeverdier</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.preview.map((item) => (
                    <Table.Tr key={item.issueObjectId}>
                      <Table.Td>{item.issueId}</Table.Td>
                      <Table.Td>{item.automaticCounterValue}</Table.Td>
                      <Table.Td>{item.newManualCounterValue}</Table.Td>
                      <Table.Td>{item.existingCounterValue ?? ""}</Table.Td>
                      <Table.Td>
                        <Code>{JSON.stringify(item.groupValues)}</Code>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
